import { cva, type VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import { forwardRef, type HTMLAttributes, type ReactNode, useId } from "react";
import { cn } from "../cn";

export const overviewCardHeaderVariants = cva("flex items-center gap-3 px-4", {
  variants: {
    size: { default: "py-3.5", small: "py-3" },
  },
  defaultVariants: { size: "default" },
});

export interface OverviewCardProps
  extends Omit<HTMLAttributes<HTMLElement>, "title">,
    VariantProps<typeof overviewCardHeaderVariants> {
  readonly title: string;
  readonly icon?: LucideIcon;
  readonly actions?: ReactNode;
}

/** Shared chrome for glanceable detail-page cards; consumers own content and state. */
export const OverviewCard = forwardRef<HTMLElement, OverviewCardProps>(
  (
    {
      title,
      icon: Icon,
      actions,
      size = "default",
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const titleId = useId();
    return (
      <section
        ref={ref}
        aria-labelledby={titleId}
        {...props}
        className={cn(
          "overflow-hidden rounded-2xl border border-border/70 bg-surface font-sans text-foreground shadow-xs",
          className,
        )}
      >
        <div className={overviewCardHeaderVariants({ size })}>
          {Icon && size !== "small" && (
            <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
              <Icon className="size-4" aria-hidden="true" />
            </span>
          )}
          <h2
            id={titleId}
            className="min-w-0 flex-1 font-sans text-sm font-semibold"
          >
            {title}
          </h2>
          {actions}
        </div>
        <div className="border-t border-border/60">{children}</div>
      </section>
    );
  },
);
OverviewCard.displayName = "OverviewCard";
